import apiClient from '@afosto/api/ApiClient';
import parseJWT from '@afosto/utils/parseJWT';
import parseUrlQuery from '@afosto/utils/parseUrlQuery';

const LOGIN_URI = 'https://login.afosto.io';

/**
 * Manages an Afosto session for a client.
 *
 * @class SessionManager
 */
export class SessionManager {
  /**
   * The base authorization uri.
   * @type {string}
   * @private
   */
  #authorizationUri = `${LOGIN_URI}/authorize`;

  /**
   * The sign out uri.
   * @type {string}
   * @private
   */
  #signOutUri = `${LOGIN_URI}/logout`;

  /**
   * The default authorization scopes.
   * @type {Array}
   * @default ['openid', 'email', 'profile']
   * @private
   */
  #defaultScopes = [
    'openid',
    'email',
    'profile',
    'iam:users:read',
    'iam:roles:read',
    'iam:tenants:read',
  ];

  /**
   * The callback handlers that should be called if the 'isAuthenticated` state changes.
   * @type {Array}
   * @private
   */
  #authenticationHandlers = [];

  /**
   * The callback handlers that should be called if the session data changes.
   * @type {Array}
   * @private
   */
  #sessionHandlers = [];

  /**
   * The callback handlers that should be called if the client data changes.
   * @type {Array}
   * @private
   */
  #clientHandlers = [];

  /**
   * The callback handlers that should be called if a session error occurs.
   * @type {Array}
   * @private
   */
  #errorHandlers = [];

  /**
   * The callback handlers that should be called if the session tenant changes.
   * @type {Array}
   * @private
   */
  #tenantHandlers = [];

  /**
   * Indicates whether the session is valid.
   * @type {boolean}
   * @default false
   * @private
   */
  #isAuthenticated = false;

  /**
   * The active client.
   * @type {null|Object}
   * @default null
   * @private
   */
  #client = null;

  /**
   * The active session.
   * @type {null|Object}
   * @default null
   * @private
   */
  #session = null;

  /**
   * The timer used for scheduling token refresh.
   * @type {null|number}
   * @default null
   * @private
   */
  #refreshTimer = null;

  /**
   * Checks whether the session manager is not being run from an iframe (silent).
   * @type {boolean}
   * @public
   */
  isRunningSilent = window.self !== window.top;

  /**
   * Indicates whether the session is authorizing.
   * @type {boolean}
   * @default true
   * @private
   */
  #isAuthorizing = !this.isRunningSilent;

  /**
   * Indicates whether the clear session is being triggered from the sign out link.
   * @type {boolean}
   */
  #isSignout = false;

  /**
   * The iframe used for silent authorization.
   * @type {null|HTMLElement}
   */
  #silentIframe = null;

  /**
   * The timer used for silent authorization.
   * @type {null|number
   * @default null
   * @private
   */
  #silentAuthorizationTimer = null;

  /**
   * The event handler used for silent authorization.
   * @type {null|function}
   * @default null
   * @private
   */
  #silentAuthorizationHandler = null;

  /**
   * The subtenant
   * @type {null|string}
   * @default null
   * @private
   */
  #subtenant = null;

  /**
   * Indicates whether during the session the tenant has been switched.
   * @type {boolean}
   * @default false
   * @private
   */
  #tenantSwitched = false;

  /**
   * The configuration for the session manager.
   * @type {{sessionKey: string, extendToken: boolean, refreshToken: boolean}}
   * @public
   */
  configuration = {
    sessionKey: 'afosto-auth_session',
    subtenantKey: 'afosto-auth_subtenant',
    shouldRefreshToken: true,
  };

  /**
   * Indicates whether the session is valid.
   * @returns {boolean}
   * @public
   */
  get isAuthenticated() {
    return this.#isAuthenticated;
  }

  /**
   * Set whether the session is valid and call the authentication handlers.
   * @public
   */
  set isAuthenticated(isAuthenticated) {
    this.#isAuthenticated = isAuthenticated || false;
    this.#authenticationHandlers.forEach(handler =>
      handler({
        isAuthenticated,
        isAuthorizing: this.isAuthorizing,
      }),
    );
  }

  /**
   * Indicates whether the session is currently authorizing (silent).
   * @public
   */
  get isAuthorizing() {
    return this.#isAuthorizing;
  }

  /**
   * Set whether the session is currently authorizing (silent).
   * @public
   */
  set isAuthorizing(isAuthorizing) {
    this.#isAuthorizing = isAuthorizing || false;
    this.#authenticationHandlers.forEach(handler =>
      handler({
        isAuthorizing,
        isAuthenticated: this.isAuthenticated,
      }),
    );
  }

  /**
   * Return the client information.
   * @returns {Object}
   * @public
   */
  get client() {
    return this.#client;
  }

  /**
   * Set and validate the client information.
   * @param {Object} client
   * @public
   */
  set client(client) {
    if (!client || !client.id) {
      throw new Error('Invalid client data');
    }

    if (client.scopes && !Array.isArray(client.scopes)) {
      throw new Error('Scopes must be provided as an array');
    }

    this.#client = {
      redirectUri: `${window.location.origin}/callback`,
      responseType: 'token id_token',
      ...client,
      scopes: [...this.#defaultScopes, ...(client.scopes || [])],
    };

    this.#clientHandlers.forEach(handler => handler(this.client));
  }

  /**
   * Return the data of the session.
   * @returns {Object}
   * @public
   */
  get session() {
    return this.#session;
  }

  /**
   * Set and validate the session data and call the session handlers.
   * @param {Object} data
   * @public
   */
  set session(data) {
    const oldSession = this.session ? { ...this.session } : null;
    let session = data;

    if (typeof session === 'string') {
      session = parseUrlQuery(session);

      if (session && session.error) {
        session = null;
      }
    }

    const { access_token: oldAccessToken } = oldSession || {};
    const { access_token: newAccessToken } = session || {};
    const { tenant: oldTenant } = oldAccessToken ? parseJWT(oldAccessToken) : {};
    const { tenant: newTenant } = newAccessToken ? parseJWT(newAccessToken) : {};

    if (session && oldTenant && oldTenant !== newTenant) {
      this.#tenantSwitched = true;
      this.#tenantHandlers.forEach(handler => handler(newTenant, oldTenant));
    } else {
      this.#session = session;
      this.#sessionHandlers.forEach(handler => handler(this.session, oldSession));
    }
  }

  /**
   * Return the access token.
   * @returns {string}
   * @public
   */
  get token() {
    const { access_token: accessToken } = this.session || {};
    return accessToken;
  }

  /**
   * Return the access token expiration time.
   * @returns {number}
   * @public
   */
  get tokenExpirationTime() {
    const currentTime = Math.floor(new Date().getTime() / 1000);
    const { exp } = this.tokenMetadata || {};

    if (!exp) {
      throw new Error('Invalid token expiration time');
    }

    return exp > currentTime ? (exp - currentTime - 300) * 1000 : 0;
  }

  /**
   * Return the metadata of the access token.
   * @returns {null|Object}
   * @public
   */
  get tokenMetadata() {
    const { access_token: accessToken } = this.session || {};
    return accessToken ? parseJWT(accessToken) : null;
  }

  /**
   * Return the user data of the id token.
   * @returns {null|Object}
   * @public
   */
  get user() {
    const { id_token: idToken } = this.session || {};

    if (!idToken) {
      return null;
    }

    const data = parseJWT(idToken);

    return {
      id: data.sub,
      email: data.email,
      emailVerified: data.email_verified,
      name: data.name,
      firstName: data.given_name,
      lastName: data.family_name,
      avatar: data.picture,
    };
  }

  get subtenant() {
    return this.#subtenant;
  }

  set subtenant(tenantID) {
    this.#subtenant = tenantID || null;
  }

  /**
   * Initialize the session manager and register all default handlers.
   */
  constructor() {
    if (this.isRunningSilent) {
      return;
    }

    this.registerAuthenticationHandler(() => {
      apiClient.setAuthorizationHeader(this.token);
    });

    apiClient.registerInterceptor((response, error) => {
      if (error && error.response && error.response.status === 401) {
        this.silentAuthorization();
      }
    });

    this.registerSessionHandler(() => {
      this.verifyIsAuthenticated();
    });

    this.registerClientHandler(client => {
      if (client && !this.isAuthenticated) {
        this.silentAuthorization();
      } else if (client) {
        this.isAuthorizing = false;
      }

      if (client && this.isAuthenticated && this.configuration.shouldRefreshToken) {
        this.checkTokenExpiration();
      }
    });

    this.registerTenantHandler(() => {
      this.cancelTokenRefresh();
    });

    this.verifyIsAuthenticated();
  }

  /**
   * Check whether the session data is valid.
   * @param {Object} session
   * @returns {boolean}
   */
  static isValidSession(session) {
    return session && session.access_token;
  }

  /**
   * Authorize the session.
   * @public
   * @returns {boolean}
   */
  authorize() {
    if (this.#isSignout) {
      return false;
    }

    this.isAuthorizing = true;

    if (!this.client) {
      throw new Error('No client configured!');
    }

    window.location.href = this.getAuthorizationUri();
    return true;
  }

  /**
   * Invalidate the session.
   * @public
   */
  signOut() {
    this.#isSignout = true;
    this.session = null;
    this.cancelTokenRefresh();
    window.location.href = this.#signOutUri;
  }

  /**
   * Remove the silent authorization element and handlers.
   * @private
   */
  removeSilentAuthorization() {
    if (this.#silentIframe) {
      window.removeEventListener('message', this.#silentAuthorizationHandler, false);

      if (this.#silentIframe.parentNode) {
        this.#silentIframe.parentNode.removeChild(this.#silentIframe);
      }

      clearTimeout(this.#silentAuthorizationTimer);
      this.#silentIframe = null;
    }
  }

  /**
   * Silently try to authorize the session.
   * @private
   */
  silentAuthorization() {
    if (!this.client) {
      throw new Error('A client is required to use silent authorization');
    }

    if (!this.isRunningSilent && !this.#silentIframe && !this.#isSignout) {
      this.isAuthorizing = true;
      this.#silentIframe = window.document.createElement('iframe');
      this.#silentIframe.name = Date.now();
      this.#silentAuthorizationHandler = ev => {
        if (ev.type === 'message' && ev.data.type === 'AUTH_CALLBACK') {
          const { data } = ev.data;

          if (data && typeof data === 'string' && data !== '' && data.indexOf('error=') < 0) {
            this.session = data;
            this.checkTokenExpiration();

            this.isAuthorizing = false;
            this.removeSilentAuthorization();
          } else {
            this.session = null;
            this.cancelTokenRefresh();
            this.removeSilentAuthorization();

            const errorData = typeof data === 'string' ? parseUrlQuery(data) : data;

            if (errorData?.error === 'No valid token supplied') {
              window.location.href = this.getAuthorizationUri();
            } else {
              this.#errorHandlers.forEach(handler => handler(errorData));
            }
          }
        }
      };
      this.#silentIframe.style.display = 'none';

      window.addEventListener('message', this.#silentAuthorizationHandler, false);
      window.document.body.appendChild(this.#silentIframe);

      this.#silentIframe.src = this.getAuthorizationUri(true);
      this.#silentAuthorizationTimer = setTimeout(() => {
        this.removeSilentAuthorization();
        this.session = null;
        this.cancelTokenRefresh();
        this.isAuthorizing = false;
      }, 60000);
    }
  }

  /**
   * Return the authorization uri.
   * @param {boolean} silent
   * @returns {string}
   * @public
   */
  getAuthorizationUri(silent = false) {
    if (!this.client) {
      throw new Error('A client is required to authorize');
    }

    const params = {
      client_id: this.#client.id,
      redirect_uri: encodeURIComponent(this.#client.redirectUri),
      response_type: this.#client.responseType,
      scope: this.#client.scopes.join(' '),
      ...(this.subtenant ? { tenant: this.subtenant } : {}),
    };

    if (silent) {
      params.silent = 1;
    }

    return Object.keys(params)
      .reduce((acc, param) => `${acc}${param}=${params[param]}&`, `${this.#authorizationUri}?`)
      .replace(/.$/, '')
      .trim();
  }

  /**
   * Check whether the session contains the provided permissions.
   * @param {Array} permissions
   * @returns {boolean}
   */
  hasPermissions(permissions = []) {
    const { scopes = [] } = this.tokenMetadata || {};
    return permissions.every(permission => scopes.includes(permission));
  }

  /**
   * Verify whether the session is valid.
   * @public
   */
  verifyIsAuthenticated() {
    this.isAuthenticated = !!(this.session && SessionManager.isValidSession(this.session));
  }

  /**
   * Check the expiration time of the access token.
   * @public
   */
  checkTokenExpiration() {
    if (this.#tenantSwitched) {
      return;
    }

    const expirationTime = this.tokenExpirationTime;

    if (this.configuration.shouldRefreshToken) {
      if (expirationTime > 0) {
        this.scheduleTokenRefresh(expirationTime);
      } else {
        this.silentAuthorization();
      }
    } else if (expirationTime <= 0) {
      this.silentAuthorization();
    }
  }

  /**
   * Cancel the token refresh timer
   * @public
   */
  cancelTokenRefresh() {
    clearTimeout(this.#refreshTimer);
  }

  /**
   * Schedule an timer to extend the lifetime of the access token.
   * @param {number} wait
   * @public
   */
  scheduleTokenRefresh(wait) {
    this.cancelTokenRefresh();
    this.#refreshTimer = setTimeout(() => this.silentAuthorization(), wait);
  }

  /**
   * Register an authentication handler.
   * @param {function} handler
   * @public
   */
  registerAuthenticationHandler(handler) {
    if (typeof handler === 'function') {
      this.#authenticationHandlers.push(handler);
    }
  }

  /**
   * Unregister the provided authentication handler.
   * @param {function} handler
   * @public
   */
  unregisterAuthenticationHandler(handler) {
    this.#authenticationHandlers = this.#authenticationHandlers.filter(item => item !== handler);
  }

  /**
   * Register a session handler.
   * @param {function} handler
   * @public
   */
  registerSessionHandler(handler) {
    if (typeof handler === 'function') {
      this.#sessionHandlers.push(handler);
    }
  }

  /**
   * Unregister the provided session handler.
   * @param {function} handler
   * @public
   */
  unregisterSessionHandler(handler) {
    this.#sessionHandlers = this.#sessionHandlers.filter(item => item !== handler);
  }

  /**
   * Register a client handler.
   * @param {function} handler
   * @public
   */
  registerClientHandler(handler) {
    if (typeof handler === 'function') {
      this.#clientHandlers.push(handler);
    }
  }

  /**
   * Unregister the provided client handler.
   * @param {function} handler
   * @public
   */
  unregisterClientHandler(handler) {
    this.#clientHandlers = this.#clientHandlers.filter(item => item !== handler);
  }

  /**
   * Register an error handler. Fired if a session error occurs.
   * @param {function} handler
   * @public
   */
  registerErrorHandler(handler) {
    if (typeof handler === 'function') {
      this.#errorHandlers.push(handler);
    }
  }

  /**
   * Unregister the provided error handler.
   * @param {function} handler
   * @public
   */
  unregisterErrorHandler(handler) {
    this.#errorHandlers = this.#errorHandlers.filter(item => item !== handler);
  }

  /**
   * Register a tenant handler. Fired if the current tenant changes.
   * @param {function} handler
   * @public
   */
  registerTenantHandler(handler) {
    if (typeof handler === 'function') {
      this.#tenantHandlers.push(handler);
    }
  }

  /**
   * Unregister the provided tenant handler.
   * @param {function} handler
   * @public
   */
  unregisterTenantHandler(handler) {
    this.#tenantHandlers = this.#tenantHandlers.filter(item => item !== handler);
  }
}

export default new SessionManager();
